import React from "react";
import { Link } from "react-router-dom";
import DataTable from "./DataTable";
import { Pill, TeamDot } from "./Ui";
import { CALL_REPORT_KIND_META, OPP_RECORD_TYPE_META, formatCurrency, formatDate, moneyOrDash } from "../metrics";

const REPORT_LABELS = {
  closedWon: "Closed Won Oppor",
  bdDeals: "BD Closed Deals",
  newClients: "BD Signed Clients",
  curateWon: "Closed Won Curate",
};

function memberLink(user, search) {
  return user && user.id ? (
    <Link to={`/landmark/bd/member/${user.id}${search}`} className="bd-link" onClick={(ev) => ev.stopPropagation()}>
      {user.name}
    </Link>
  ) : (
    <span className="bd-muted">{(user && user.name) || "—"}</span>
  );
}

/**
 * Closed-won opportunities table. Each row shows its Record Type and which
 * BD opportunity report(s) it belongs to.
 */
export function DealsTable({ rows = [], loading, search = "", showOwner = false, showTeam = false, pageSize = 25 }) {
  return (
    <DataTable
      loading={loading}
      rows={rows}
      rowKey={(r) => r.sfId}
      defaultSort={{ key: "closeDate", dir: "desc" }}
      pageSize={pageSize}
      dense
      stickyHeader={false}
      emptyTitle="No closed-won opportunities in this period"
      columns={[
        { key: "closeDate", label: "Closed", metric: "wonDeals", width: 105, render: (r) => formatDate(r.closeDate, { year: false }), sortValue: (r) => r.closeDate },
        {
          key: "recordTypeName",
          label: "Type",
          metric: "dealsWon",
          width: 120,
          render: (r) => {
            const m = OPP_RECORD_TYPE_META[r.recordTypeName] || { short: r.recordTypeName || "—", tone: "neutral" };
            return (
              <Pill tone={m.tone} title={`Record Type: ${r.recordTypeName || "—"} · Reports: ${(r.reports || []).map((k) => REPORT_LABELS[k] || k).join(", ") || "none"}`}>
                {m.short}
              </Pill>
            );
          },
        },
        {
          key: "name",
          label: "Opportunity",
          render: (r) => (
            <span className="bd-person__text">
              <span className="bd-ellipsis" title={r.name}>
                {r.name}
              </span>
              <small>{[r.accountName, r.clientName].filter(Boolean).join(" · ") || "—"}</small>
            </span>
          ),
        },
        ...(showOwner ? [{ key: "owner", label: "Owner", hideBelow: "md", sortValue: (r) => r.owner && r.owner.name, render: (r) => memberLink(r.owner, search) }] : []),
        ...(showTeam ? [{ key: "team", label: "Team", hideBelow: "md", sortValue: (r) => r.team, render: (r) => <TeamDot team={r.team} withName /> }] : []),
        { key: "landmarkTeamName", label: "Landmark team", hideBelow: "lg", render: (r) => r.landmarkTeamName || r.clientOwner || <span className="bd-muted">—</span> },
        { key: "amount", label: "Amount", metric: "revenue", align: "right", render: (r) => moneyOrDash(r.amount, false) },
        {
          key: "contractValue",
          label: "Contract value",
          metric: "contractValueSum",
          align: "right",
          hideBelow: "lg",
          render: (r) => (r.contractValue ? formatCurrency(r.contractValue) : <span className="bd-muted">—</span>),
        },
        {
          key: "retainerFee",
          label: "Retainer",
          metric: "retainerFeeSum",
          align: "right",
          hideBelow: "lg",
          render: (r) => (r.retainerFee ? formatCurrency(r.retainerFee) : <span className="bd-muted">—</span>),
        },
      ]}
    />
  );
}

/**
 * Call reports (Salesforce Tasks) table - the rows behind "Meetings held".
 */
export function CallReportsTable({ rows = [], loading, search = "", showOwner = false, showTeam = false, pageSize = 25 }) {
  return (
    <DataTable
      loading={loading}
      rows={rows}
      rowKey={(r) => r.sfId}
      defaultSort={{ key: "activityDate", dir: "desc" }}
      pageSize={pageSize}
      dense
      stickyHeader={false}
      emptyTitle="No call reports in this period"
      emptyMessage="Call reports are Salesforce Tasks whose Subject starts with 'TAP Notes' or contains 'Call Notes'."
      columns={[
        { key: "activityDate", label: "Date", metric: "meetingsHappened", width: 105, render: (r) => formatDate(r.activityDate, { year: false }), sortValue: (r) => r.activityDate },
        {
          key: "kind",
          label: "Type",
          width: 130,
          render: (r) => {
            const m = CALL_REPORT_KIND_META[r.kind] || { label: r.kind || "—", tone: "neutral" };
            return <Pill tone={m.tone}>{m.label}</Pill>;
          },
        },
        {
          key: "subject",
          label: "Subject",
          render: (r) => (
            <span className="bd-person__text">
              <span className="bd-ellipsis" title={r.subject}>
                {r.subject}
              </span>
              <small>{[r.whoName, r.whatName].filter(Boolean).join(" · ") || "—"}</small>
            </span>
          ),
        },
        ...(showOwner ? [{ key: "owner", label: "Assigned", hideBelow: "md", sortValue: (r) => r.owner && r.owner.name, render: (r) => memberLink(r.owner, search) }] : []),
        ...(showTeam ? [{ key: "team", label: "Team", hideBelow: "md", sortValue: (r) => r.team, render: (r) => <TeamDot team={r.team} withName /> }] : []),
        { key: "meetingType", label: "Meeting type", hideBelow: "lg", render: (r) => r.meetingType || <span className="bd-muted">—</span> },
        {
          key: "bdWpPointType",
          label: "WP point",
          hideBelow: "lg",
          render: (r) => (r.bdWpPointId ? <Pill tone="info">{r.bdWpPointType || "linked"}</Pill> : <span className="bd-muted">—</span>),
        },
      ]}
    />
  );
}
